import { useCallback, useEffect, useState } from 'react';
import { API } from '../service/ProductsService';
import ProductRow from './ProductRow';
import ProductListModal from './ProductListModal';

// הנתונים משתנים רק בייבוא, ולכן נשמרים לכל משך הביקור (חזרה לדשבורד אחרי חיפוש לא טוענת שוב)
let cached = null;

const formatNumber = (n) => Number(n || 0).toLocaleString('he-IL');

// כשעוד אין היסטוריית חיפושים (אתר חדש / DB ריק)
const FALLBACK_SEARCHES = ['דבש', 'חלב תנובה', 'במבה', 'חלה'];

const s = {
  wrap:      { display: 'flex', flexDirection: 'column', gap: 28 },
  tiles:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 },
  // עמודה: "לרשימה" נדחף לתחתית, כך שבכל המשבצות הוא באותו גובה (כמו "מעבר למוצר" ב-ProductRow)
  tile:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', textAlign: 'right', color: 'inherit', font: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' },
  // אותו עיצוב כמו storeLink ב-ProductRow. alignSelf: flex-end = צד שמאל בעמוד RTL
  tileLink:  { alignSelf: 'flex-end', marginTop: 'auto', padding: '6px 12px', fontSize: 13, fontWeight: 600, color: '#fff', background: '#1a3f9c', borderRadius: 8, whiteSpace: 'nowrap' },
  tileBody:  { marginBottom: 10 },
  tileLabel: { fontSize: 13, color: '#59636e' },
  tileValue: { fontSize: 28, fontWeight: 700, lineHeight: 1.2, marginTop: 4 },
  tileNote:  { fontSize: 12, color: '#8c959f', marginTop: 2 },
  section:   { display: 'flex', flexDirection: 'column', gap: 10 },
  heading:   { margin: 0, fontSize: 18, fontWeight: 700 },
  subhead:   { margin: '-6px 0 0', fontSize: 13, color: '#59636e' },
  pair:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 28 },
  message:   { textAlign: 'center', color: '#59636e', padding: '32px 16px' },
  popular:   { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: '#59636e', fontSize: 14 },
  chip:      { padding: '4px 12px', borderRadius: 999, border: '1px solid #d0d7de', background: '#fff', color: '#1f2328', fontSize: 14 },
};

// החיפושים הנפוצים באתר. לחיצה מריצה את החיפוש
const PopularSearches = ({ searches, onSearch }) => {
  const hasHistory = searches.length > 0;
  const list = hasHistory ? searches.map((x) => x.query) : FALLBACK_SEARCHES;
  return (
    <div style={s.popular}>
      <span>{hasHistory ? 'מחפשים הכי הרבה:' : 'הקלידו שם מוצר או חברה ולחצו חיפוש. למשל:'}</span>
      {list.map((query) => (
        <button key={query} style={s.chip} onClick={() => onSearch(query)}>{query}</button>
      ))}
    </div>
  );
};

// משבצת. onClick — פותחת את רשימת המוצרים שמאחוריה (ProductListModal)
const Tile = ({ label, value, note, onClick }) => (
  <button type="button" style={s.tile} onClick={onClick} aria-haspopup="dialog">
    <div style={s.tileBody}>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue} className="num">{value}</div>
      {note && <div style={s.tileNote}>{note}</div>}
    </div>
    {/* span ולא button — כל המשבצת היא כבר כפתור */}
    <span style={s.tileLink}>לרשימה</span>
  </button>
);

const Section = ({ title, subtitle, items, stores, pricesByItem }) => {
  if (!items.length) return null;
  return (
    <section style={s.section}>
      <h2 style={s.heading}>{title}</h2>
      {subtitle && <p style={s.subhead}>{subtitle}</p>}
      {items.map((item) => (
        <ProductRow key={item.id} item={item} stores={stores} prices={pricesByItem[item.id] || {}} />
      ))}
    </section>
  );
};

// דשבורד בעמוד הראשי (כשאין חיפוש). הנתונים מ-spGetDashboard (server/src/SQL/Products.sql)
// onSearch(text): מריץ חיפוש (לחיצה על חיפוש פופולרי)
const Dashboard = ({ onSearch }) => {
  const [data, setData] = useState(cached);
  const [error, setError] = useState(null);
  const [listModal, setListModal] = useState(null);   // { title, subtitle, list, storeId } — ר' ProductListModal
  // useCallback: ProductListModal נרשם ל-Escape לפי onClose
  const closeList = useCallback(() => setListModal(null), []);

  useEffect(() => {
    if (cached) return;
    API.getDashboard()
      .then((resp) => {
        const status = resp?.[0]?.[0];
        if (status?.code !== 0) {
          setError(status?.msg || 'שגיאה בטעינת הנתונים');
          return;
        }
        // [1] stores, [2] summary, [3] storeStats, [4] items (עם section), [5] prices, [6] topSearches
        const pricesByItem = {};
        (resp[5] || []).forEach((p) => {
          if (!pricesByItem[p.itemId]) pricesByItem[p.itemId] = {};
          pricesByItem[p.itemId][p.storeId] = p;
        });
        const items = resp[4] || [];
        const bySection = (section) => items.filter((i) => i.section === section);
        cached = {
          stores: resp[1] || [],
          summary: resp[2]?.[0] || {},
          storeStats: resp[3] || [],
          topDiff: bySection('topDiff'),
          mostExpensive: bySection('mostExpensive'),
          cheapest: bySection('cheapest'),
          pricesByItem,
          topSearches: resp[6] || [],
        };
        setData(cached);
      })
      .catch((err) => setError(err.message || 'שגיאה בטעינת הנתונים'));
  }, []);

  // בזמן טעינה / שגיאה — עדיין מציגים דוגמאות חיפוש, כדי שהעמוד לא יהיה ריק
  if (error || !data) {
    return (
      <div style={s.wrap}>
        <PopularSearches searches={[]} onSearch={onSearch} />
        <div style={s.message}>{error || 'טוען נתונים…'}</div>
      </div>
    );
  }

  const { stores, summary, storeStats, pricesByItem } = data;
  const storeName = (id) => stores.find((st) => st.id === id)?.name || '';

  return (
    <div style={s.wrap}>
      <PopularSearches searches={data.topSearches} onSearch={onSearch} />
      <div style={s.tiles}>
        <Tile
          label="מוצרים בהשוואה"
          value={formatNumber(summary.comparedCount)}
          note={`נמכרים ביותר מחנות אחת, מתוך ${formatNumber(summary.itemCount)}`}
          onClick={() => setListModal({ title: 'מוצרים בהשוואה', subtitle: 'נמכרים ביותר מחנות אחת · ההפרש הגדול קודם', list: 'compared' })}
        />
        {storeStats.map((st) => (
          <Tile
            key={st.storeId}
            label={`זול יותר ב${storeName(st.storeId)}`}
            value={formatNumber(st.cheaperCount)}
            note="מוצרים"
            onClick={() => setListModal({
              title: `זול יותר ב${storeName(st.storeId)}`,
              subtitle: `המוצרים ש${storeName(st.storeId)} הכי זולה בהם · החיסכון הגדול קודם`,
              list: 'cheaper',
              storeId: st.storeId,
            })}
          />
        ))}
        <Tile
          label="אותו מחיר"
          value={formatNumber(summary.samePriceCount)}
          note="מוצרים"
          onClick={() => setListModal({ title: 'אותו מחיר', subtitle: 'מוצרים שעולים אותו דבר בכל החנויות', list: 'same' })}
        />
        <Tile
          label="פער ממוצע"
          value={`${Number(summary.avgDiffPct || 0)}%`}
          note="בין החנויות, במוצרים המשותפים"
          onClick={() => setListModal({ title: 'פערי מחירים', subtitle: 'המוצרים עם הפער הגדול ביותר באחוזים', list: 'gap' })}
        />
      </div>

      <Section
        title="5 ההפרשים הגדולים"
        subtitle="המוצרים שבהם הכי משתלם לבחור את החנות הנכונה"
        items={data.topDiff}
        stores={stores}
        pricesByItem={pricesByItem}
      />

      <div style={s.pair}>
        <Section title="המוצר היקר ביותר" subtitle="מבין המוצרים שנמכרים ביותר מחנות אחת" items={data.mostExpensive} stores={stores} pricesByItem={pricesByItem} />
        <Section title="המוצר הזול ביותר" subtitle="מבין המוצרים שנמכרים ביותר מחנות אחת" items={data.cheapest} stores={stores} pricesByItem={pricesByItem} />
      </div>

      {listModal && (
        <ProductListModal
          title={listModal.title}
          subtitle={listModal.subtitle}
          list={listModal.list}
          storeId={listModal.storeId}
          onClose={closeList}
        />
      )}
    </div>
  );
};

export default Dashboard;
